import {
  Button,
  colors,
  Modal,
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  NotificationLevel,
  notify,
  spacing,
  Text,
  Tooltip,
} from '@retailer-platform/shared-components'
import React, { useState } from 'react'
import faker from 'faker'
import styled from '@emotion/styled'
import { SwitchBase } from '@instacart/ids-tooling'
import { HelpIcon } from '@instacart/ids-core'
import { Divider } from '@retailer-platform/shared-components/src/tds'
import { useDomainMessages } from '../../../utils/domain/intl'
import { useRunManualStepTestMutation } from '../../../api'
import { TooltipContainerNotFullWidth } from '../../shared-styled-components/SharedStyledComponents'
import { SwitchRow } from './LLMPromptTemplateRunManualTestModal'

interface Props {
  onClose: () => void
  stepVersionId: string
}

const StyledModalBody = styled(ModalBody)({
  gap: `${spacing.X8}`,
})

const SnowflakeQueryInputRunManualTestModal: React.FC<Props> = ({ onClose, stepVersionId }) => {
  const i18n = useDomainMessages({
    submit: 'catalogAdminDomain.attributeManagement.runTests.modal.button.submit',
    cancel: 'catalogAdminDomain.attributeManagement.runTests.modal.button.cancel',
    title: 'catalogAdminDomain.attributeManagement.runManualTest.modal.snowflake.title',
    description: 'catalogAdminDomain.attributeManagement.runManualTest.modal.snowflake.description',
    batchSizeTooltip:
      'catalogAdminDomain.attributeManagement.runTests.modal.body.tooltip.batchSize',
    runType: 'catalogAdminDomain.attributeManagement.runResults.modal.subheader.runType',
    limitedBatchTest:
      'catalogAdminDomain.attributeManagement.runResults.modal.body.limitedBatchTest',
    largeBatchTest: 'catalogAdminDomain.attributeManagement.runResults.modal.body.largeBatchTest',
  })

  const [isProductionMode, setIsProductionMode] = useState(false)
  const [triggerManualTest, { loading }] = useRunManualStepTestMutation({
    variables: {
      clientId: 'catalog_camp_ipp',
      idempotencyKey: faker.random.uuid(),
      stepVersionId: stepVersionId,
      isTest: !isProductionMode,
    },
    onError: e => {
      notify({
        level: NotificationLevel.Error,
        contents: `Error running manual test: ${e.message}`,
      })
    },
    onCompleted: () => {
      window.location.reload()
      onClose()
    },
  })
  return (
    <Modal isOpen={true} hideCloseButton={true} onRequestClose={onClose}>
      <ModalContainer>
        <ModalHeader>{i18n.title}</ModalHeader>
        <StyledModalBody>
          <SwitchRow>
            <Text css={{ fontWeight: 'bold' }}>{i18n.runType} </Text>
            <TooltipContainerNotFullWidth>
              <Tooltip placement="bottom" target={<HelpIcon />}>
                Running this step in test mode will limit results to 5000 rows.
              </Tooltip>
            </TooltipContainerNotFullWidth>
          </SwitchRow>
          <SwitchRow>
            <Text
              css={{
                fontWeight: 600,
                color: isProductionMode ? colors.GRAYSCALE.X30 : colors.SECONDARY.DARK,
              }}
            >
              {' '}
              Test Mode
            </Text>
            <SwitchBase
              checked={isProductionMode}
              onChange={() => setIsProductionMode(!isProductionMode)}
              description="Run a large batch"
            />
            <Text
              css={{
                fontWeight: 600,
                color: isProductionMode ? colors.SECONDARY.DARK : colors.GRAYSCALE.X30,
              }}
            >
              {' '}
              Production Mode
            </Text>
          </SwitchRow>
          <Divider></Divider>
          <Text size="medium">
            Starts a manual run of this step. It will run the query and create tasks based on the
            step&apos;s configuration. Running in test mode will limit tasks created to 5000 rows.
          </Text>
        </StyledModalBody>
        <ModalFooter>
          <Button variant={'primary'} disabled={loading} onClick={() => triggerManualTest()}>
            {i18n.submit}
          </Button>
          <Button variant={'secondary'} onClick={() => onClose()}>
            {i18n.cancel}
          </Button>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  )
}

export default SnowflakeQueryInputRunManualTestModal
